export type MediaFileType = 'image' | 'audio' | 'video' | 'document' | 'other';

export interface MediaAsset {
  id: number;
  key: string;
  name: string;
  fileType: MediaFileType;
  mimeType: string;
  fileSize: number;
  fileSizeLabel?: string;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  fetchApiPath: string;
  fetchApiUrl: string;
  listApiPath: string;
  listApiUrl: string;
  sampleResponse?: {
    success: boolean;
    message: string;
    data: {
      id: number;
      key: string;
      name: string;
      fileUrl: string;
      fileType: MediaFileType;
      mimeType: string;
      isActive: boolean;
      version: number;
      updatedAt: string;
    };
  };
}

/** Public API shape (what apps receive when calling fetchApiUrl) */
export interface PublicMediaAsset {
  id: number;
  key: string;
  name: string;
  fileUrl: string;
  fileType: MediaFileType;
  mimeType: string;
  isActive: boolean;
  version: number;
  updatedAt: string;
}

export interface MediaAssetMeta {
  fileTypes: MediaFileType[];
  keyPattern: string;
  fetchApiExample: string;
  listApiExample: string;
}

export interface MediaAssetListResponse {
  data: MediaAsset[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UploadMediaAssetPayload {
  file: File;
  key: string;
  name?: string;
}

export interface UpdateMediaAssetPayload {
  name?: string;
  isActive?: boolean;
  file?: File;
}

export function fileTypeLabel(type: MediaFileType): string {
  return (
    {
      image: 'Image',
      audio: 'Audio',
      video: 'Video',
      document: 'Document',
      other: 'Other',
    } as Record<MediaFileType, string>
  )[type];
}

export function normalizeMediaKeyInput(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

export function isValidMediaKey(key: string): boolean {
  return /^[a-z][a-z0-9_]{2,63}$/.test(key);
}

export function buildMediaFetchPath(key: string): string {
  return `/api/media/key/${encodeURIComponent(key)}`;
}

export function buildMediaListPath(): string {
  return '/api/media';
}

function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }
  return '';
}

export function buildMediaFetchUrl(key: string): string {
  const path = buildMediaFetchPath(key);
  const base = resolveApiBaseUrl();
  return base ? `${base}${path}` : path;
}

export function buildMediaListUrl(): string {
  const path = buildMediaListPath();
  const base = resolveApiBaseUrl();
  return base ? `${base}${path}` : path;
}

export function buildMediaSampleResponse(asset: Pick<
  MediaAsset,
  'id' | 'key' | 'name' | 'fileType' | 'mimeType' | 'isActive' | 'version' | 'updatedAt'
>) {
  return {
    success: true,
    message: 'Media asset fetched successfully',
    data: {
      id: asset.id,
      key: asset.key,
      name: asset.name,
      fileUrl: '<resolved-at-runtime>',
      fileType: asset.fileType,
      mimeType: asset.mimeType,
      isActive: asset.isActive,
      version: asset.version,
      updatedAt: asset.updatedAt,
    },
  };
}

/** Normalize API payloads (camelCase or legacy snake_case) and fill integration fields. */
export function normalizeMediaAsset(raw: unknown): MediaAsset {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const key = String(row.key ?? row.asset_key ?? row.purpose ?? '');
  const fileType = (row.fileType ?? row.file_type ?? 'other') as MediaFileType;
  const id = Number(row.id ?? 0);
  const version = Number(row.version ?? 1);
  const updatedAt = String(row.updatedAt ?? row.updated_at ?? new Date().toISOString());
  const createdAt = String(row.createdAt ?? row.created_at ?? updatedAt);
  const isActive = Boolean(row.isActive ?? row.is_active ?? true);

  const fetchApiPath = String(row.fetchApiPath ?? buildMediaFetchPath(key));
  const listApiPath = String(row.listApiPath ?? buildMediaListPath());
  const fetchApiUrl = String(row.fetchApiUrl ?? buildMediaFetchUrl(key));
  const listApiUrl = String(row.listApiUrl ?? buildMediaListUrl());

  const base: MediaAsset = {
    id,
    key,
    name: String(row.name ?? ''),
    fileType,
    mimeType: String(row.mimeType ?? row.mime_type ?? ''),
    fileSize: Number(row.fileSize ?? row.file_size ?? 0),
    fileSizeLabel: row.fileSizeLabel
      ? String(row.fileSizeLabel)
      : row.file_size_label
        ? String(row.file_size_label)
        : undefined,
    isActive,
    version,
    createdAt,
    updatedAt,
    fetchApiPath,
    fetchApiUrl,
    listApiPath,
    listApiUrl,
  };

  const sampleResponse = row.sampleResponse as MediaAsset['sampleResponse'] | undefined;
  return {
    ...base,
    sampleResponse: sampleResponse?.data ? sampleResponse : buildMediaSampleResponse(base),
  };
}
